export const LEAD_VALUES = {
  // Low-intent / Link click actions
  SOCIAL_JOIN: 5, // Joins telegram/whatsapp/instagram
  EXTERNAL_LINK: 1, // User clicked external event site link

  TICKET_LOW: 5, // Ticket link click for low-price events
  TICKET_DEFAULT: 15, // Default ticket click
  TICKET_HIGH: 50, // Ticket link click for high-price events (Bagatelle/Mandala VIP)

  EVENT_RESERVATION_LOW: 35, // Low-tier table inquiry
  EVENT_RESERVATION: 120, // Standard Cabo table/bottle service inquiry
  EVENT_RESERVATION_HIGH: 150, // Premium Cabo venues (Taboo, Bagatelle, Rosa Negra, Crania)

  // Medium-intent / Lead inquiries
  GENERAL_INQUIRY: 80, // Header RSVP, general contact concierge
};

export const KEYWORD_VALUE_MAPPING = {
  // High-tier venues / brands in Los Cabos
  taboo: {
    reservation: LEAD_VALUES.EVENT_RESERVATION_HIGH,
    ticket: LEAD_VALUES.TICKET_HIGH,
  },
  bagatelle: {
    reservation: LEAD_VALUES.EVENT_RESERVATION_HIGH,
    ticket: LEAD_VALUES.TICKET_HIGH,
  },
  'rosa-negra': {
    reservation: LEAD_VALUES.EVENT_RESERVATION_HIGH,
    ticket: LEAD_VALUES.TICKET_HIGH,
  },
  rosanegra: {
    reservation: LEAD_VALUES.EVENT_RESERVATION_HIGH,
    ticket: LEAD_VALUES.TICKET_HIGH,
  },
  crania: {
    reservation: LEAD_VALUES.EVENT_RESERVATION_HIGH,
    ticket: LEAD_VALUES.TICKET_HIGH,
  },
  chambao: {
    reservation: LEAD_VALUES.EVENT_RESERVATION_HIGH,
    ticket: LEAD_VALUES.TICKET_HIGH,
  },
  mandala: {
    reservation: LEAD_VALUES.EVENT_RESERVATION_HIGH,
    ticket: LEAD_VALUES.TICKET_HIGH,
  },
  sundream: {
    reservation: LEAD_VALUES.EVENT_RESERVATION_HIGH,
    ticket: LEAD_VALUES.TICKET_HIGH,
  },

  // Mid-tier venues / brands
  ilios: {
    reservation: LEAD_VALUES.EVENT_RESERVATION,
    ticket: LEAD_VALUES.TICKET_DEFAULT,
  },
  mamazzita: {
    reservation: LEAD_VALUES.EVENT_RESERVATION,
    ticket: LEAD_VALUES.TICKET_DEFAULT,
  },
  confessions: {
    reservation: LEAD_VALUES.EVENT_RESERVATION,
    ticket: LEAD_VALUES.TICKET_DEFAULT,
  },
  'el-squid-roe': {
    reservation: LEAD_VALUES.EVENT_RESERVATION,
    ticket: LEAD_VALUES.TICKET_DEFAULT,
  },
  squid: {
    reservation: LEAD_VALUES.EVENT_RESERVATION,
    ticket: LEAD_VALUES.TICKET_DEFAULT,
  },
  veleros: {
    reservation: LEAD_VALUES.EVENT_RESERVATION,
    ticket: LEAD_VALUES.TICKET_DEFAULT,
  },
  'la-vaquita': {
    reservation: LEAD_VALUES.EVENT_RESERVATION,
    ticket: LEAD_VALUES.TICKET_DEFAULT,
  },
  craft: {
    reservation: LEAD_VALUES.EVENT_RESERVATION,
    ticket: LEAD_VALUES.TICKET_DEFAULT,
  },
  crush: {
    reservation: LEAD_VALUES.EVENT_RESERVATION,
    ticket: LEAD_VALUES.TICKET_DEFAULT,
  },
  beach: {
    reservation: LEAD_VALUES.EVENT_RESERVATION_HIGH,
    ticket: LEAD_VALUES.TICKET_DEFAULT,
  },
};

/**
 * Resolves the appropriate lead value based on a keyword (venue/brand name or slug)
 * and the event type ('reservation' or 'ticket').
 *
 * @param {string} keyword - The venue slug, brand name, or label to check
 * @param {string} type - The type of interaction: 'reservation' or 'ticket'
 * @param {number} fallbackValue - The fallback value if no keyword matches
 * @returns {number} The resolved conversion value
 */
export function getLeadValueByKeyword(
  keyword,
  type = 'reservation',
  fallbackValue = null
) {
  if (!keyword || typeof keyword !== 'string') {
    if (fallbackValue !== null) return fallbackValue;
    return type === 'ticket'
      ? LEAD_VALUES.TICKET_DEFAULT
      : LEAD_VALUES.EVENT_RESERVATION;
  }

  const normalizedKeyword = keyword.toLowerCase();

  for (const [key, config] of Object.entries(KEYWORD_VALUE_MAPPING)) {
    if (normalizedKeyword.includes(key)) {
      if (config[type] !== undefined) {
        return config[type];
      }
    }
  }

  if (fallbackValue !== null) {
    return fallbackValue;
  }
  return type === 'ticket'
    ? LEAD_VALUES.TICKET_DEFAULT
    : LEAD_VALUES.EVENT_RESERVATION;
}

/**
 * Tracks a ticket or reservation lead event to Google Analytics (GA4/gtag) and Rollbar.
 *
 * @param {string} slug - The event slug or venue keyword (e.g. currentEvent.slug or party.slug)
 * @param {string} type - The interaction type: 'tickets' or 'reservations'
 */
export function trackEventLead(slug, type = 'reservations') {
  const resolvedType =
    type.toLowerCase() === 'tickets' ? 'ticket' : 'reservation';
  const val = getLeadValueByKeyword(slug, resolvedType);
  const gaCategory = 'Event';
  const gaAction = resolvedType === 'ticket' ? 'Tickets' : 'reservations';

  // 1. Google Analytics (Legacy UA if present)
  if (typeof window.ga === 'function') {
    window.ga('send', 'event', gaCategory, gaAction, slug, val);
  }

  // 2. Google Analytics 4 (gtag)
  if (typeof window.gtag === 'function') {
    window.gtag('event', gaAction, {
      event_category: gaCategory,
      event_label: slug,
      value: val,
      currency: 'USD',
    });
  }

  // 3. Rollbar lead logging
  if (window.Rollbar && typeof window.Rollbar.info === 'function') {
    window.Rollbar.info(`Conversion Lead: ${gaAction} - ${slug}`, {
      slug,
      type: resolvedType,
      value: val,
      currency: 'USD',
    });
  }
}
